import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Company Roadmap — visualizes the OKR tree as a radial constellation
// (North Star center → Objectives orbiting → KRs branching → linked Goals
// as leaf dots) and an alternate quarterly timeline view. Same data
// powers both: GET /okr/objectives is the alignment tree the Alignment
// tab already uses, so this tab adds zero backend.
//
// Visibility is gated upstream in Goals.tsx (same gate as Company OKRs —
// admin / manager / owner). The view inside doesn't re-check perms.
//
// Click any node to drill into the side detail panel. Hover dims
// unrelated branches so a complex tree stays readable.
import { notify } from "../Toast";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useAuth } from "../../hooks/useAuth";
// Default text shown until an admin/owner sets one. Mirrors the
// backend's DEFAULT_NORTH_STAR string. Kept short so the constellation
// centerpiece doesn't get cluttered.
const DEFAULT_NORTH_STAR = "Set your company's North Star";
// --------------------------------------------------------------------------
// Health colors — pulled from the dark glassmorphism token set in
// styles.css so the constellation reads consistently with the rest of
// the goals page. (Cannot read CSS vars from inside an SVG via JS
// without parsing computed styles, so we hard-code hex values that
// match.)
const HEALTH_HEX = {
    on_track: "#4ade80",
    at_risk: "#fbbf24",
    behind: "#f87171",
};
const HEALTH_LABEL = {
    on_track: "On track",
    at_risk: "At risk",
    behind: "Behind",
};
// --------------------------------------------------------------------------
// Geometry — center, ring radii. Tuned for the 980×620 viewBox below.
const SVG_W = 980;
const SVG_H = 620;
const CX = 490;
const CY = 310;
const R_OBJ = 170;
const R_KR = 270;
function objPct(o) {
    return Math.round(o.pct);
}
function buildLayout(objectives) {
    const nodes = [];
    const links = [];
    // North Star at center always. The 1-Objective case widens the KR fan
    // below so the orbiting KRs don't bunch above the lone Objective; the
    // North Star still sits at center as the orchid centerpiece.
    nodes.push({
        id: "ns",
        kind: "ns",
        x: CX,
        y: CY,
        r: 50,
        health: "on_track",
        pct: null,
        label: "North Star",
        parentId: null,
    });
    const n = Math.max(objectives.length, 1);
    objectives.forEach((o, i) => {
        const a = ((-90 + i * (360 / n)) * Math.PI) / 180;
        const ox = CX + R_OBJ * Math.cos(a);
        const oy = CY + R_OBJ * Math.sin(a);
        nodes.push({
            id: o.id,
            kind: "obj",
            x: ox,
            y: oy,
            r: 34,
            health: o.health,
            pct: objPct(o),
            label: o.title,
            parentId: "ns",
            ref: o,
        });
        links.push({ from: "ns", to: o.id, health: o.health });
        // Key Results fanned around the objective's angle. When there's only
        // one Objective, widen the fan to ~220° so 4+ KRs distribute around
        // the canvas instead of clustering above the Objective. Multi-Objective
        // case keeps the tight 30° fan so each Objective's KRs stay near it.
        const m = o.key_results.length;
        const spread = objectives.length === 1
            ? (220 * Math.PI) / 180
            : (30 * Math.PI) / 180;
        o.key_results.forEach((k, j) => {
            const ka = a + (m === 1 ? 0 : -spread / 2 + j * (spread / (m - 1)));
            const kx = CX + R_KR * Math.cos(ka);
            const ky = CY + R_KR * Math.sin(ka);
            nodes.push({
                id: k.id,
                kind: "kr",
                x: kx,
                y: ky,
                r: 23,
                health: k.health,
                pct: Math.round(k.pct),
                label: k.title,
                parentId: o.id,
                ref: k,
            });
            links.push({ from: o.id, to: k.id, health: k.health });
            // Linked goals as outer leaf dots
            k.linked_goals.forEach((g, z) => {
                const ga = ka + (-10 + z * 12) * (Math.PI / 180);
                const gr = R_KR + 58 + (z % 2) * 16;
                const gx = CX + gr * Math.cos(ga);
                const gy = CY + gr * Math.sin(ga);
                nodes.push({
                    id: `${k.id}-g${z}`,
                    kind: "goal",
                    x: gx,
                    y: gy,
                    r: 7,
                    // No per-goal health in the OkrLinkedGoal payload — inherit
                    // the parent KR's health so the leaf color tells you whether
                    // it's contributing to a healthy or struggling KR.
                    health: k.health,
                    pct: null,
                    label: g.title,
                    parentId: k.id,
                    ref: g,
                });
                links.push({ from: k.id, to: `${k.id}-g${z}`, health: k.health });
            });
        });
    });
    return { nodes, links };
}
// Curved connector — pulled slightly toward center for an organic arc.
function curve(a, b) {
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = mx - CX;
    const dy = my - CY;
    const k = 0.12;
    return `M ${a.x} ${a.y} Q ${mx - dx * k} ${my - dy * k} ${b.x} ${b.y}`;
}
// Naive word-wrap into <tspan>s. We cap on character count rather than
// measured width — fine for short titles, the SVG isn't trying to be a
// typography engine.
function wrapLabel(str, max) {
    const words = str.split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
        if ((line + " " + w).trim().length > max) {
            lines.push(line.trim());
            line = w;
        }
        else {
            line += " " + w;
        }
    }
    if (line.trim())
        lines.push(line.trim());
    return lines.slice(0, 2); // cap at 2 lines so labels don't sprawl
}
// --------------------------------------------------------------------------
export default function RoadmapTab() {
    const { user } = useAuth();
    const canEditNorthStar = user?.role === "admin" || user?.role === "owner";
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [objectives, setObjectives] = useState([]);
    const [view, setView] = useState("constellation");
    // selectedId drives the detail panel; "ns" / null means "show overview".
    const [selectedId, setSelectedId] = useState("ns");
    const [hoverId, setHoverId] = useState(null);
    // North Star state — fetched alongside objectives, editable inline by
    // admin/owner via a small modal that opens on double-click of the
    // centerpiece (or the "✎ Edit" pill near the legend).
    const [northStar, setNorthStar] = useState({
        value: DEFAULT_NORTH_STAR,
        updated_at: null,
        is_default: true,
    });
    const [editingNs, setEditingNs] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [objs, ns] = await Promise.all([
                    api.get("/okr/objectives"),
                    api
                        .get("/me/company-settings/north-star")
                        .catch(() => null),
                ]);
                if (!cancelled) {
                    setObjectives(objs.objectives ?? []);
                    if (ns)
                        setNorthStar(ns);
                }
            }
            catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : "Couldn't load the roadmap");
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    async function saveNorthStar(value) {
        const trimmed = value.trim();
        if (!trimmed)
            return;
        try {
            const r = await api.patch("/me/company-settings/north-star", { value: trimmed });
            setNorthStar(r);
            setEditingNs(false);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't save the North Star.");
        }
    }
    const { nodes, links } = useMemo(() => buildLayout(objectives), [objectives]);
    const nodeById = useMemo(() => {
        const m = new Map();
        for (const n of nodes)
            m.set(n.id, n);
        return m;
    }, [nodes]);
    // KPI counters along the top — same shape as the mockup.
    const kpis = useMemo(() => {
        const allKrs = objectives.flatMap((o) => o.key_results);
        const allGoals = allKrs.flatMap((k) => k.linked_goals);
        const objOn = objectives.filter((o) => o.health === "on_track").length;
        const krBehind = allKrs.filter((k) => k.health === "behind").length;
        return {
            objectives: objectives.length,
            objectivesOnTrack: objOn,
            keyResults: allKrs.length,
            krsBehind: krBehind,
            goals: allGoals.length,
        };
    }, [objectives]);
    // Hover dimming: keep the selected branch + its ancestors and
    // children at full opacity, dim everything else.
    const dimSet = useMemo(() => {
        if (!hoverId)
            return null;
        const keep = new Set(["ns", hoverId]);
        // Walk up to root
        let cur = nodeById.get(hoverId);
        while (cur && cur.parentId) {
            keep.add(cur.parentId);
            cur = nodeById.get(cur.parentId);
        }
        // Add children of anything in keep
        for (const n of nodes) {
            if (n.parentId && keep.has(n.parentId))
                keep.add(n.id);
        }
        return keep;
    }, [hoverId, nodeById, nodes]);
    // Detail panel target. Goals don't have a useful standalone view
    // (their model is sparse), so clicking a goal drills into its KR.
    const detailId = useMemo(() => {
        const n = nodeById.get(selectedId);
        if (!n)
            return "ns";
        if (n.kind === "goal" && n.parentId)
            return n.parentId;
        return selectedId;
    }, [selectedId, nodeById]);
    function select(id) {
        setSelectedId(id);
    }
    if (loading)
        return _jsx("div", { className: "muted", children: "Loading the roadmap\u2026" });
    if (error)
        return _jsx("div", { className: "error", children: error });
    if (objectives.length === 0) {
        return (_jsx("div", { className: "muted", style: { padding: "1.5rem 0" }, children: "No active objectives yet \u2014 create one on the Alignment tab to populate the roadmap." }));
    }
    return (_jsxs("div", { className: "okr-roadmap", children: [_jsxs("div", { className: "okr-roadmap-kpis", children: [_jsx(KPI, { value: String(kpis.objectives), label: "Objectives" }), _jsx(KPI, { value: `${kpis.objectivesOnTrack}/${kpis.objectives}`, label: "Objectives on track", tone: "ok" }), _jsx(KPI, { value: String(kpis.keyResults), label: "Key Results" }), _jsx(KPI, { value: String(kpis.krsBehind), label: "KRs behind", tone: kpis.krsBehind > 0 ? "behind" : "ok" }), _jsx(KPI, { value: String(kpis.goals), label: "Linked goals" })] }), _jsxs("div", { className: `okr-northstar-pill ${northStar.is_default ? "is-default" : ""}`, title: canEditNorthStar
                    ? "Double-click to edit your company's North Star"
                    : northStar.is_default
                        ? "No North Star set yet — ask an admin or owner to set one."
                        : "Your company's North Star", onDoubleClick: () => {
                    if (canEditNorthStar)
                        setEditingNs(true);
                }, children: [_jsx("span", { className: "okr-northstar-kicker", children: "North Star" }), _jsx("span", { className: "okr-northstar-value", children: northStar.value }), canEditNorthStar && (_jsx("button", { type: "button", className: "okr-northstar-edit", onClick: () => setEditingNs(true), title: "Set the North Star", "aria-label": "Set the North Star", children: "\u270E" }))] }), _jsxs("div", { className: "okr-roadmap-toolbar", children: [_jsxs("div", { className: "okr-roadmap-seg", children: [_jsx("button", { className: view === "constellation" ? "active" : "", onClick: () => setView("constellation"), children: "\u2726 Constellation" }), _jsx("button", { className: view === "timeline" ? "active" : "", onClick: () => setView("timeline"), children: "\u25A6 Quarterly timeline" })] }), _jsxs("div", { className: "okr-roadmap-legend", children: [_jsxs("span", { className: "okr-roadmap-lg", children: [_jsx("span", { className: "okr-roadmap-dot", style: { background: HEALTH_HEX.on_track } }), "On track"] }), _jsxs("span", { className: "okr-roadmap-lg", children: [_jsx("span", { className: "okr-roadmap-dot", style: { background: HEALTH_HEX.at_risk } }), "At risk"] }), _jsxs("span", { className: "okr-roadmap-lg", children: [_jsx("span", { className: "okr-roadmap-dot", style: { background: HEALTH_HEX.behind } }), "Behind"] })] })] }), _jsxs("div", { className: "okr-roadmap-stage", children: [view === "constellation" ? (_jsx(Constellation, { nodes: nodes, links: links, nodeById: nodeById, selectedId: selectedId, dimSet: dimSet, northStar: northStar, canEditNorthStar: canEditNorthStar, onSelect: select, onHover: setHoverId, onEditNorthStar: () => setEditingNs(true) })) : (_jsx(Timeline, { objectives: objectives, onSelectObjective: select })), _jsx(DetailPanel, { objectives: objectives, targetId: detailId, nodeById: nodeById, onSelect: select })] }), editingNs && (_jsx(NorthStarEditor, { initial: northStar.is_default ? "" : northStar.value, onCancel: () => setEditingNs(false), onSave: saveNorthStar }))] }));
}
// --------------------------------------------------------------------------
// Inline editor — a small modal so the SVG centerpiece doesn't need to
// host an overlaid input. Admin/owner only; the parent already gates the
// open call. Saves on Enter / button; Escape or click-outside cancels.
function NorthStarEditor({ initial, onCancel, onSave, }) {
    const [draft, setDraft] = useState(initial);
    const [saving, setSaving] = useState(false);
    async function submit() {
        if (saving)
            return;
        const trimmed = draft.trim();
        if (!trimmed)
            return;
        setSaving(true);
        try {
            await onSave(trimmed);
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: (e) => e.target === e.currentTarget && onCancel(), children: _jsxs("div", { className: "modal okr-northstar-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Set your North Star" }), _jsx("p", { className: "muted", style: { marginTop: "-0.5rem", fontSize: "0.85rem" }, children: "The company-wide vision or theme that everything ladders up to. Shows in the center of the Roadmap constellation." }), _jsxs("label", { children: ["North Star", _jsx("input", { type: "text", autoFocus: true, value: draft, maxLength: 240, placeholder: "e.g. Become the operating system for independent artists", onChange: (e) => setDraft(e.target.value), onKeyDown: (e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    submit();
                                }
                                else if (e.key === "Escape") {
                                    e.preventDefault();
                                    onCancel();
                                }
                            } })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: onCancel, disabled: saving, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: submit, disabled: saving || !draft.trim(), children: saving ? "Saving…" : "Save" })] })] }) }));
}
// --------------------------------------------------------------------------
// KPI tile
function KPI({ value, label, tone, }) {
    return (_jsxs("div", { className: "okr-roadmap-kpi", children: [_jsx("div", { className: `okr-roadmap-kpi-n ${tone ?? ""}`, children: value }), _jsx("div", { className: "okr-roadmap-kpi-l", children: label })] }));
}
// --------------------------------------------------------------------------
// Constellation SVG
function Constellation({ nodes, links, nodeById, selectedId, dimSet, northStar, canEditNorthStar, onSelect, onHover, onEditNorthStar, }) {
    return (_jsx("div", { className: "okr-roadmap-canvas", children: _jsxs("svg", { viewBox: `0 0 ${SVG_W} ${SVG_H}`, className: "okr-constellation", children: [_jsx("defs", { children: _jsxs("radialGradient", { id: "nsGrad", cx: "35%", cy: "30%", r: "80%", children: [_jsx("stop", { offset: "0%", stopColor: "#f5b8ef" }), _jsx("stop", { offset: "55%", stopColor: "#da70d6" }), _jsx("stop", { offset: "100%", stopColor: "#7d0448" })] }) }), links.map((l, i) => {
                    const a = nodeById.get(l.from);
                    const b = nodeById.get(l.to);
                    if (!a || !b)
                        return null;
                    const dim = dimSet !== null && !(dimSet.has(l.from) && dimSet.has(l.to));
                    return (_jsx("path", { d: curve(a, b), stroke: HEALTH_HEX[l.health], fill: "none", strokeWidth: 1.6, opacity: dim ? 0.12 : 0.5 }, `${l.from}-${l.to}-${i}`));
                }), nodes.map((nd) => {
                    const dim = dimSet !== null && !dimSet.has(nd.id);
                    const selected = nd.id === selectedId;
                    return (_jsx(NodeShape, { node: nd, dim: dim, selected: selected, northStar: northStar, canEditNorthStar: canEditNorthStar, onSelect: onSelect, onHover: onHover, onEditNorthStar: onEditNorthStar }, nd.id));
                })] }) }));
}
function NodeShape({ node, dim, selected, northStar, canEditNorthStar, onSelect, onHover, onEditNorthStar, }) {
    const fill = node.kind === "ns"
        ? "url(#nsGrad)"
        : node.kind === "goal"
            ? HEALTH_HEX[node.health]
            : "rgba(7,29,71,0.95)";
    const stroke = node.kind === "ns" ? "rgba(255,255,255,.35)" : HEALTH_HEX[node.health];
    const strokeW = node.kind === "goal" ? 0 : selected ? 3 : 2;
    // Progress ring for obj + kr
    const showRing = (node.kind === "obj" || node.kind === "kr") && node.pct !== null;
    const ringR = node.r - 6;
    const circ = 2 * Math.PI * ringR;
    const ringDash = node.pct !== null ? (circ * node.pct) / 100 : 0;
    // Outside labels for obj + kr. Coordinates are RELATIVE to the node
    // (the parent <g> already has transform=translate(node.x, node.y)).
    // Earlier this used absolute coords inside the translated group, which
    // double-counted the node position and pushed every label off-canvas —
    // that's why the right-side KR label cut off at the canvas edge and
    // the bottom KR label hung below it.
    const showLabel = node.kind === "obj" || node.kind === "kr";
    let labelEl = null;
    if (showLabel) {
        const ang = Math.atan2(node.y - CY, node.x - CX);
        const off = node.kind === "obj" ? 14 : 11;
        const lx = Math.cos(ang) * (node.r + off);
        const ly = Math.sin(ang) * (node.r + off);
        const anchor = Math.cos(ang) > 0.25 ? "start" : Math.cos(ang) < -0.25 ? "end" : "middle";
        const lines = wrapLabel(node.label, node.kind === "obj" ? 18 : 20);
        const baseY = ly - (lines.length - 1) * 6;
        labelEl = (_jsx("text", { x: lx, y: ly, textAnchor: anchor, className: node.kind === "obj" ? "okr-c-label" : "okr-c-label sm", fontSize: node.kind === "obj" ? 12.5 : 11, children: lines.map((ln, i) => (_jsx("tspan", { x: lx, y: baseY + i * 12.5, children: ln }, i))) }));
    }
    return (_jsxs("g", { className: `okr-c-node ${dim ? "dim" : ""} ${selected ? "selected" : ""}`, transform: `translate(${node.x},${node.y})`, onClick: (e) => {
            e.stopPropagation();
            onSelect(node.id);
        }, onDoubleClick: (e) => {
            // Double-click the North Star centerpiece → open the editor.
            // Admin/owner only; for everyone else this falls through to the
            // single-click select() above (which already fired). Same UX
            // pattern as the task-title rename on Projects & Meetings.
            if (node.kind === "ns" && canEditNorthStar) {
                e.stopPropagation();
                onEditNorthStar();
            }
        }, onMouseEnter: () => onHover(node.id), onMouseLeave: () => onHover(null), style: { cursor: "pointer" }, children: [_jsx("circle", { r: node.r, fill: fill, stroke: stroke, strokeWidth: strokeW }), showRing && (_jsxs(_Fragment, { children: [_jsx("circle", { r: ringR, fill: "none", stroke: "rgba(255,255,255,0.12)", strokeWidth: 3 }), _jsx("circle", { r: ringR, fill: "none", stroke: HEALTH_HEX[node.health], strokeWidth: 3, strokeDasharray: `${ringDash} ${circ}`, strokeLinecap: "round", transform: "rotate(-90)" })] })), node.kind === "ns" && (() => {
                // North Star centerpiece: kicker label on top + the configured
                // North Star text wrapped to 2-3 short lines. Long titles get
                // truncated with an ellipsis so the orchid circle stays clean —
                // the full text shows in the toolbar pill above the canvas.
                const lines = wrapLabel(northStar.value, 14).slice(0, 3);
                const fontSize = lines.length <= 1 ? 12 : lines.length === 2 ? 11 : 10;
                const lineGap = lines.length <= 1 ? 0 : fontSize + 1;
                const baseY = -((lines.length - 1) * lineGap) / 2 + 3;
                return (_jsxs(_Fragment, { children: [_jsx("text", { x: 0, y: -((lines.length - 1) * lineGap) / 2 - 14, textAnchor: "middle", fontSize: 9.5, className: "okr-c-pct", style: { letterSpacing: "0.16em" }, children: "NORTH STAR" }), lines.map((ln, i) => (_jsx("text", { x: 0, y: baseY + i * lineGap, textAnchor: "middle", fontSize: fontSize, className: "okr-c-pct", style: { fontWeight: 700, fill: "#fff" }, children: ln }, i)))] }));
            })(), node.kind === "obj" && node.pct !== null && (_jsxs("text", { x: 0, y: 5, textAnchor: "middle", fontSize: 15, className: "okr-c-pct", children: [node.pct, "%"] })), node.kind === "kr" && node.pct !== null && (_jsxs("text", { x: 0, y: 4, textAnchor: "middle", fontSize: 12, className: "okr-c-pct", children: [node.pct, "%"] })), labelEl] }));
}
// --------------------------------------------------------------------------
// Timeline view — quarter lanes per objective, KR pills positioned by
// the objective's target_date (we don't have per-KR dates today).
function targetQuarter(o) {
    // Map an objective's target_date month to Q1–Q4 of its own year.
    // Falls back to Q4 when missing so it still appears on the lane.
    if (!o.target_date)
        return 4;
    const m = new Date(o.target_date).getMonth(); // 0-11
    return Math.floor(m / 3) + 1;
}
function targetYear(o) {
    if (!o.target_date)
        return new Date().getFullYear();
    return new Date(o.target_date).getFullYear();
}
function Timeline({ objectives, onSelectObjective, }) {
    const now = new Date();
    const thisYear = now.getFullYear();
    // "Now" line position within the year, in percent (0–100).
    const yearStart = new Date(thisYear, 0, 1).getTime();
    const yearEnd = new Date(thisYear + 1, 0, 1).getTime();
    const nowPct = Math.max(0, Math.min(100, ((now.getTime() - yearStart) / (yearEnd - yearStart)) * 100));
    return (_jsx("div", { className: "okr-roadmap-canvas", children: _jsxs("div", { className: "okr-timeline", children: [_jsxs("div", { className: "okr-timeline-head", children: [_jsx("div", { className: "okr-timeline-corner", children: "Objective" }), [1, 2, 3, 4].map((q) => (_jsxs("div", { className: "okr-timeline-qh", children: ["Q", q, " ", thisYear] }, q)))] }), objectives.map((o) => {
                    const q = targetQuarter(o);
                    const y = targetYear(o);
                    // Only objectives ending this year get positioned by quarter;
                    // others land on Q4 with a "earlier/later" hint.
                    const offYear = y !== thisYear;
                    const leftPct = ((q - 1) / 4) * 100;
                    const widthPct = 22;
                    return (_jsxs("div", { className: "okr-timeline-row", onClick: () => onSelectObjective(o.id), children: [_jsxs("div", { className: "okr-timeline-obj", children: [_jsxs("div", { className: "okr-timeline-obj-title", children: [_jsx("span", { className: "okr-timeline-accent", style: { background: HEALTH_HEX[o.health] } }), _jsx("span", { children: o.title })] }), _jsxs("span", { className: "okr-timeline-objmeta", children: [o.business_unit_name ?? "Company", " \u00B7 ", Math.round(o.pct), "%"] })] }), _jsxs("div", { className: "okr-timeline-lanes", children: [[1, 2, 3, 4].map((qq) => (_jsx("div", { className: "okr-timeline-cell" }, qq))), _jsxs("div", { className: "okr-timeline-bar", style: {
                                            left: `calc(${leftPct}% + 4px)`,
                                            width: `calc(${widthPct}% - 8px)`,
                                            background: HEALTH_HEX[o.health],
                                        }, title: `${o.title}${offYear ? ` (target ${o.target_date})` : ""}`, children: [_jsx("i", { className: "okr-timeline-fill", style: { width: `${Math.round(o.pct)}%` } }), _jsxs("span", { children: [o.key_results.length, " KR", o.key_results.length === 1 ? "" : "s"] })] }), _jsx("div", { className: "okr-timeline-now", style: { left: `${nowPct}%` } })] })] }, o.id));
                })] }) }));
}
// --------------------------------------------------------------------------
// Detail panel
function DetailPanel({ objectives, targetId, nodeById, onSelect, }) {
    if (targetId === "ns" || !nodeById.has(targetId)) {
        const objOn = objectives.filter((o) => o.health === "on_track").length;
        return (_jsxs("aside", { className: "okr-roadmap-detail", children: [_jsx("div", { className: "okr-roadmap-detail-kicker", children: "North Star" }), _jsx("h3", { children: "All objectives this period" }), _jsx("div", { className: "okr-roadmap-meta", children: _jsxs("span", { className: "okr-roadmap-chip ok", children: [_jsx("span", { className: "okr-roadmap-dot", style: { background: HEALTH_HEX.on_track } }), objOn, "/", objectives.length, " on track"] }) }), _jsx("p", { className: "muted", style: { margin: "4px 0 0" }, children: "Click any node in the constellation (or a row in Timeline) to drill in. Every Objective owns a business unit; its Key Results pull live progress from goals, KPIs, or the CRM, and the small dots orbiting each KR are the goals laddering up to it." }), objectives.map((o) => (_jsxs("div", { className: "okr-roadmap-kr", style: { cursor: "pointer" }, onClick: () => onSelect(o.id), children: [_jsxs("h4", { children: [_jsx("span", { children: o.title }), _jsxs("span", { className: "v", style: { color: HEALTH_HEX[o.health] }, children: [Math.round(o.pct), "%"] })] }), _jsx(Bar, { pct: o.pct, health: o.health })] }, o.id)))] }));
    }
    const node = nodeById.get(targetId);
    if (node.kind === "obj") {
        const o = node.ref;
        return (_jsxs("aside", { className: "okr-roadmap-detail", children: [_jsx("div", { className: "okr-roadmap-detail-kicker", children: "Objective" }), _jsx("h3", { children: o.title }), _jsxs("div", { className: "okr-roadmap-meta", children: [_jsx(Chip, { health: o.health }), _jsx("span", { className: "okr-roadmap-tag", children: o.business_unit_name ?? "Company" }), _jsxs("span", { className: "okr-roadmap-tag", children: [Math.round(o.pct), "% complete"] }), o.target_date && (_jsxs("span", { className: "okr-roadmap-tag", children: ["Target ", o.target_date] }))] }), o.description && (_jsx("p", { className: "muted", style: { marginTop: 8, fontSize: 12.5 }, children: o.description })), o.key_results.map((k) => (_jsx(KrBlock, { kr: k, onSelect: onSelect }, k.id))), _jsx("p", { className: "muted okr-roadmap-hint", children: "Objective rollup = mean of its Key Results. Each KR draws live progress from its goals, a KPI metric, or the CRM pipeline." })] }));
    }
    if (node.kind === "kr") {
        const k = node.ref;
        const parentObj = objectives.find((o) => o.key_results.some((kr) => kr.id === k.id));
        return (_jsxs("aside", { className: "okr-roadmap-detail", children: [_jsxs("div", { className: "okr-roadmap-detail-kicker", children: ["Key Result \u00B7 ", parentObj?.title ?? ""] }), _jsx("h3", { children: k.title }), _jsxs("div", { className: "okr-roadmap-meta", children: [_jsx(Chip, { health: k.health }), parentObj && (_jsx("span", { className: "okr-roadmap-tag", children: parentObj.business_unit_name ?? "Company" })), _jsxs("span", { className: "okr-roadmap-tag", children: [Math.round(k.current_value), k.target_value != null ? ` / ${Math.round(k.target_value)}` : "", k.unit ? ` ${k.unit}` : ""] })] }), _jsx(Bar, { pct: k.pct, health: k.health }), _jsx("div", { className: "okr-roadmap-detail-kicker", style: { marginTop: 6 }, children: "Goals laddering up" }), k.linked_goals.length === 0 ? (_jsx("p", { className: "muted", style: { fontSize: 12.5, margin: "4px 0 0" }, children: "No goals linked yet. The Alignment tab is where you ladder them up." })) : (_jsx("ul", { className: "okr-roadmap-goals", children: k.linked_goals.map((g) => (_jsxs("li", { children: [_jsx("span", { className: "muted small", children: g.owner_name ?? "—" }), " · ", _jsx("span", { children: g.title })] }, g.id))) })), _jsx("p", { className: "muted okr-roadmap-hint", children: "Goals are the work that moves this number. Add or unlink goals from the Alignment tab." })] }));
    }
    return null;
}
function Bar({ pct, health }) {
    return (_jsx("div", { className: "okr-roadmap-bar", children: _jsx("i", { style: {
                width: `${Math.max(0, Math.min(100, pct))}%`,
                background: HEALTH_HEX[health],
            } }) }));
}
function Chip({ health }) {
    return (_jsxs("span", { className: `okr-roadmap-chip ${health}`, children: [_jsx("span", { className: "okr-roadmap-dot", style: { background: HEALTH_HEX[health] } }), HEALTH_LABEL[health]] }));
}
function KrBlock({ kr, onSelect, }) {
    return (_jsxs("div", { className: "okr-roadmap-kr", style: { cursor: "pointer" }, onClick: () => onSelect(kr.id), children: [_jsxs("h4", { children: [_jsx("span", { children: kr.title }), _jsxs("span", { className: "v", children: [Math.round(kr.current_value), kr.target_value != null ? `/${Math.round(kr.target_value)}` : "", kr.unit ? ` ${kr.unit}` : ""] })] }), _jsx(Bar, { pct: kr.pct, health: kr.health }), kr.linked_goals.length > 0 && (_jsxs("div", { className: "okr-roadmap-kr-goals", children: [kr.linked_goals.length, " linked goal", kr.linked_goals.length === 1 ? "" : "s"] }))] }));
}
